import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Popover } from "@shared/v2/Popover";
import Avatar from "@shared/v2/Avatar";
import { CaretDownSolidV6 } from "@shared/v2/Icomoon";
import { updatePrimaryAgentOrPond } from "../serviceHelpers";
import { useGetAgentState } from "../agentOrPondHookHelpers";
import "../../Filters/components/PondOrAgentFilter/styles.scss";
import { updatePrimaryAgent } from "../../PersonDetail/actions/personDetailActionCreators";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";

const AgentOrPondSelect = ({
  personId,
  primaryAgent,
  setPrimaryAgent,
  setLeadPond,
  leadPond,
  small,
  onBeforeAgentChange,
  onBeforePondChange,
  onChangeSuccess,
  triggerClasses,
  nameClasses,
  caretClasses,
}) => {
  const featureFlags = useFeatureFlags();
  const [state, setState, handleChange] = useGetAgentState(featureFlags.leadPond);

  const dispatch = useDispatch();

  const onChange = (params, onSuccess) => {
    updatePrimaryAgentOrPond({
      data: params,
      errorCb: () => {},
      successCb: (response) => {
        const newUuid = response.data.primary_agent_uuid;
        dispatch(updatePrimaryAgent(newUuid));
        const agent = state.allAssignableAgents.find((a) => a.uuid === newUuid);
        if (agent) {
          setPrimaryAgent(agent);
        }
        onSuccess?.();
        onChangeSuccess?.();
      },
    });
  };

  const handleAgentSelect = (agent) => {
    const params = {
      primary_agent: agent.id.toString(),
      lead_pond: null,
      people_ids: [personId.toString()],
    };

    if (onBeforeAgentChange) {
      const shouldProceed = onBeforeAgentChange(agent, params);
      if (!shouldProceed) return;
    }

    onChange(params, () => {
      setLeadPond(null);
    });
  };

  const handlePondSelect = (pond) => {
    const params = {
      primary_agent: null,
      lead_pond: pond.id,
      people_ids: [personId.toString()],
    };

    if (onBeforePondChange) {
      const shouldProceed = onBeforePondChange(pond, params);
      if (!shouldProceed) return;
    }

    onChange(params, () => {
      setLeadPond(pond);
    });
  };

  const { filter, filteredAssignableAgents, filteredAssignablePonds } = state;
  return (
    <Popover placement="bottom" maxHeight={400} forceTriggerWidth>
      <Popover.Trigger>
        {(leadPond || primaryAgent) && (
          <button
            className={triggerClasses}
            type="button"
            onClick={() => setState({ ...state, show: true })}
          >
            <Avatar
              src={leadPond ? null : primaryAgent.image_src}
              alt={leadPond?.name || primaryAgent?.name}
              size={small ? "small" : "medium"}
            />
            <div className={nameClasses}>
              <div className="tw-text-14d tw-font-semibold tw-truncate">
                {leadPond?.name || primaryAgent?.name}
              </div>
              {!small && (
                <small className="tw-font-semibold tw-text-12d tw-text-gray-50">
                  {!leadPond ? "Primary Agent" : "Lead Pond"}
                </small>
              )}
            </div>
            <span
              className={`${caretClasses} ${small ? "" : "tw-bg-gray-10 tw-h-[24px] tw-w-[24px] tw-rounded-full"
                }`}
            >
              <CaretDownSolidV6 />
            </span>
          </button>
        )}
      </Popover.Trigger>

      <Popover.Content className="tw-max-h-[400px]">
        <div className="tw-p-4">
          <input
            className="tw-w-full tw-border tw-border-gray-10"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={handleChange}
            placeholder="Search..."
            value={filter}
          />
        </div>

        {filteredAssignablePonds?.length > 0 && (
          <p className="tw-text-[12px] tw-py-1 tw-px-2 tw-bg-neutral-gray-10">Pond</p>
        )}
        {filteredAssignablePonds?.map((pond) => (
          <Popover.Item key={pond.id} onClick={() => handlePondSelect(pond)}>
            <div className="tw-overflow-hidden tw-flex tw-items-center tw-gap-[8px]" title={pond.name}>
              <Avatar src={pond.image_src} alt={pond.name} />
              <div className="tw-flex-1 tw-whitespace-nowrap [text-overflow:ellipsis] tw-overflow-hidden">
                {pond.name}
              </div>
            </div>
          </Popover.Item>
        ))}

        {filteredAssignableAgents?.length > 0 && (
          <p className="tw-text-[12px] tw-py-1 tw-px-2 tw-bg-neutral-gray-10">Team</p>
        )}
        {filteredAssignableAgents?.map((agent) => (
          <Popover.Item key={agent.id} onClick={() => handleAgentSelect(agent)}>
            <div className="tw-overflow-hidden tw-flex tw-items-center tw-gap-[8px]" title={agent.name}>
              <Avatar src={agent.image_src} alt={agent.name} />
              <div className="tw-flex-1 tw-whitespace-nowrap [text-overflow:ellipsis] tw-overflow-hidden">
                {agent.name}
              </div>
            </div>
          </Popover.Item>
        ))}
      </Popover.Content>
    </Popover>
  );
};

AgentOrPondSelect.propTypes = {
  personId: PropTypes.number.isRequired,
  primaryAgent: PropTypes.shape(),
  leadPond: PropTypes.shape(),
  setLeadPond: PropTypes.func,
  setPrimaryAgent: PropTypes.func,
  small: PropTypes.bool,
  onBeforeAgentChange: PropTypes.func,
  onBeforePondChange: PropTypes.func,
  onChangeSuccess: PropTypes.func,
  triggerClasses: PropTypes.string,
  nameClasses: PropTypes.string,
  caretClasses: PropTypes.string,
};
AgentOrPondSelect.defaultProps = {
  primaryAgent: null,
  leadPond: null,
  small: false,
  setLeadPond: null,
  setPrimaryAgent: null,
  onBeforeAgentChange: null,
  onBeforePondChange: null,
  onChangeSuccess: null,
  triggerClasses:
    "tw-bg-transparent tw-border-0 tw-flex tw-items-center tw-gap-[14px] tw-w-full tw-overflow-hidden tw-p-0",
  nameClasses: "tw-flex-1 tw-overflow-hidden tw-text-left",
  caretClasses: "tw-ml-4 tw-flex tw-items-center tw-justify-center",
};

export default AgentOrPondSelect;
